import Grid from '@mui/material/Grid';
import { type GeoLocationsList, GeoLocationTypes } from '@openx/types/geoLocation';
import { GeographicOption, type GeographicTargetingState, type RadiusCircle } from '@openx/types/targeting/geographic';
import { noop } from '@openx/utils/noop';
import isEmpty from 'lodash/isEmpty';
import { useCallback } from 'react';
import { DrawerTopBox } from '../../shared/TargetingDrawer/DrawerTopBox';
import type { TargetingItemsProps } from '../../types';
import { useGeographicOptions } from '../hooks/useGeographicOptions';
import { LocationType } from './LocationType';

export const GeographicTopBox = ({
  targetingParams,
  onChange = noop,
  option,
  setOption,
}: TargetingItemsProps<GeographicTargetingState> & {
  option: GeographicOption;
  setOption: (option: GeographicOption) => void;
}) => {
  let opt = option;
  if (
    option === GeographicOption.INCLUDES &&
    Object.values(targetingParams.includes).every(isEmpty) &&
    !Object.values(targetingParams.excludes).every(isEmpty)
  ) {
    // INCLUDES by default, in case when there are only excludes items we switch to EXCLUDES
    opt = GeographicOption.EXCLUDES;
  }

  const optionDropdown = <LocationType value={option} onChange={setOption} />;

  const onBulkApplyPostalCode = useCallback(
    (items: GeoLocationsList) => {
      const postalCodeValue = targetingParams[opt][GeoLocationTypes.POSTAL_CODE]
        ? [...targetingParams[opt][GeoLocationTypes.POSTAL_CODE]]
        : [];

      onChange({
        ...targetingParams,
        [opt]: {
          ...targetingParams[opt],
          [GeoLocationTypes.POSTAL_CODE]: [...postalCodeValue, ...items],
        },
      });
    },
    [onChange, opt, targetingParams]
  );

  const onBulkApplyCoordinates = useCallback(
    (items: RadiusCircle[]) => {
      const circlesValue = targetingParams[GeographicOption.CIRCLES]
        ? [...targetingParams[GeographicOption.CIRCLES]]
        : [];

      onChange({
        ...targetingParams,
        [GeographicOption.CIRCLES]: [...circlesValue, ...items],
      });
    },
    [onChange, targetingParams]
  );

  const onSelect = useCallback(
    item => {
      switch (opt) {
        case GeographicOption.CIRCLES: {
          const circlesValue = targetingParams[opt] ? [...targetingParams[opt]] : [];

          onChange({
            ...targetingParams,
            [opt]: [...circlesValue, item],
          });

          break;
        }

        case GeographicOption.SOURCE: {
          onChange(params => ({ ...params, [GeographicOption.SOURCE]: item }));
          break;
        }

        case GeographicOption.BOUNDING_BOX: {
          onChange(params => ({ ...params, [GeographicOption.BOUNDING_BOX]: item }));
          break;
        }

        default: {
          const sectionValue = targetingParams[opt][item.type] ? [...targetingParams[opt][item.type]] : [];

          onChange({
            ...targetingParams,
            [opt]: {
              ...targetingParams[opt],
              [item.type]: [...sectionValue, item],
            },
          });
        }
      }
    },
    [onChange, opt, targetingParams]
  );

  const { optionsTypes } = useGeographicOptions({
    targetingParams,
    optionDropdown,
    onSelect,
    onBulkApplyPostalCode,
    onBulkApplyCoordinates,
  });

  return (
    <DrawerTopBox>
      <Grid
        sx={{
          flexGrow: 1,
        }}
      >
        {optionsTypes[option]}
      </Grid>
    </DrawerTopBox>
  );
};
